import _ from 'lodash';
import { TFunction } from 'react-i18next';

import { Charger } from '../../../stores/types';
import { ChargerSelectionList } from '../../../stores/types/chargers.interface';
import {
  PromotionFormErrors,
  PromotionFormState,
} from '../../../stores/types/promotionForm.interface';
import {
  Promotion,
  PromotionCreateRequest,
  PromotionDiscountType,
  PromotionResourceType,
  PromotionUpdateRequest,
} from '../../../stores/types/promotions.interface';
import {
  ALL_CHARGERS_ID,
  OPTIONAL_PROMOTION_LANGUAGES,
  PROMOTION_LANGUAGES,
  PROMOTION_VALUE_MAX,
  PROMOTION_VALUE_MIN,
  PromotionLanguage,
  RECEIPT_LABEL_MAX_LENGTH,
} from './promotionForm.constants';

type ApplicableResources = Partial<Record<PromotionResourceType, string[]>>;

export type ClearableErrorField = keyof Omit<PromotionFormErrors, 'labels'> | PromotionLanguage;

const TOP_LEVEL_ERROR_KEYS: ReadonlyArray<keyof Omit<PromotionFormErrors, 'labels'>> = [
  'name',
  'validFrom',
  'validUntil',
  'discountValue',
  'chargers',
];

export const isTopLevelErrorKey = (
  field: ClearableErrorField,
): field is keyof Omit<PromotionFormErrors, 'labels'> =>
  (TOP_LEVEL_ERROR_KEYS as ReadonlyArray<string>).includes(field);

export const clearFieldError = (
  errors: PromotionFormErrors,
  field: ClearableErrorField,
): PromotionFormErrors =>
  isTopLevelErrorKey(field)
    ? { ...errors, [field]: undefined }
    : { ...errors, labels: { ...errors.labels, [field]: undefined } };

export const buildChargerSelectionList = (
  chargers: Charger[],
  t: TFunction,
  locationNameToId: Map<string, string> = new Map(),
): ChargerSelectionList[] => {
  const groupedByLocationName = _.groupBy(chargers, 'location.name');

  const groups: ChargerSelectionList[] = Object.entries(groupedByLocationName)
    .filter(([, locationChargers]) => locationChargers.length > 0)
    .map(([locationName, locationChargers]) => ({
      id: locationNameToId.get(locationName) ?? locationName,
      label: locationName,
      selected: false,
      children: locationChargers.map((charger) => ({
        id: charger.id,
        label: charger.name ?? charger.displayName,
        selected: false,
      })),
    }));

  if (groups.length === 0) return [];

  const allChargersGroup: ChargerSelectionList = {
    id: ALL_CHARGERS_ID,
    label: t('all_chargers'),
    selected: false,
    children: [],
  };
  return [allChargersGroup, ...groups];
};

export const buildApplicableResources = (
  groups: ChargerSelectionList[],
): ApplicableResources | null => {
  const locationIds: string[] = [];
  const chargerIds: string[] = [];

  groups.forEach((group) => {
    if (group.id === ALL_CHARGERS_ID) return;
    const selectedChildren = group.children.filter((child) => child.selected);
    if (selectedChildren.length === 0) return;

    if (selectedChildren.length === group.children.length) {
      locationIds.push(group.id);
    } else {
      selectedChildren.forEach((child) => chargerIds.push(child.id));
    }
  });

  if (locationIds.length === 0 && chargerIds.length === 0) return null;

  const result: ApplicableResources = {};
  if (locationIds.length) result[PromotionResourceType.LOCATION] = locationIds;
  if (chargerIds.length) result[PromotionResourceType.CHARGER] = chargerIds;
  return result;
};

export const hasAnyChargerSelected = (groups: ChargerSelectionList[]): boolean =>
  groups.some(
    (group) => group.id !== ALL_CHARGERS_ID && group.children.some((child) => child.selected),
  );

export const buildFormStateFromPromotion = (
  promotion: Promotion,
  chargerList: ChargerSelectionList[],
): PromotionFormState => {
  const savedChargerIds = new Set(
    promotion.applicableResources[PromotionResourceType.CHARGER] ?? [],
  );
  const savedLocationIds = new Set(
    promotion.applicableResources[PromotionResourceType.LOCATION] ?? [],
  );

  let allSelectableChargersSelected = true;
  let hasAnySelectableGroup = false;

  const chargers = chargerList.map((group) => {
    if (group.id === ALL_CHARGERS_ID) return group;
    hasAnySelectableGroup = true;
    const isLocationSelected = savedLocationIds.has(group.id);
    const children = group.children.map((charger) => {
      const selected = isLocationSelected || savedChargerIds.has(charger.id);
      if (!selected) allSelectableChargersSelected = false;
      return { ...charger, selected };
    });
    return { ...group, children };
  });

  const allSelected = hasAnySelectableGroup && allSelectableChargersSelected;
  const normalized = chargers.map((g) =>
    g.id === ALL_CHARGERS_ID ? { ...g, selected: allSelected } : g,
  );

  return {
    name: promotion.name,
    validFrom: promotion.validFrom ? new Date(promotion.validFrom) : null,
    validUntil: promotion.validUntil ? new Date(promotion.validUntil) : null,
    discountType: promotion.discountType,
    discountValue: String(promotion.discountValue),
    chargers: normalized,
    labels: {
      [PROMOTION_LANGUAGES.ENGLISH]: promotion.label[PROMOTION_LANGUAGES.ENGLISH] ?? '',
      [PROMOTION_LANGUAGES.FRENCH]: promotion.label[PROMOTION_LANGUAGES.FRENCH] ?? '',
      [PROMOTION_LANGUAGES.SPANISH]: promotion.label[PROMOTION_LANGUAGES.SPANISH] ?? '',
    },
    visibleOptionalLanguages: OPTIONAL_PROMOTION_LANGUAGES.filter(
      (lang) => !!promotion.label[lang],
    ),
  };
};

export const buildInitialFormState = (chargers: ChargerSelectionList[]): PromotionFormState => ({
  name: '',
  validFrom: null,
  validUntil: null,
  discountType: PromotionDiscountType.PERCENTAGE,
  discountValue: '',
  chargers,
  labels: {
    [PROMOTION_LANGUAGES.ENGLISH]: '',
    [PROMOTION_LANGUAGES.FRENCH]: '',
    [PROMOTION_LANGUAGES.SPANISH]: '',
  },
  visibleOptionalLanguages: [],
});

export const validatePromotionForm = (
  state: PromotionFormState,
  t: TFunction,
): PromotionFormErrors => {
  const errors: PromotionFormErrors = { labels: {} };

  if (!state.name.trim()) {
    errors.name = t('required_field');
  }

  if (!state.validFrom) {
    errors.validFrom = t('required_field');
  }

  if (state.validFrom && state.validUntil && state.validUntil < state.validFrom) {
    errors.validFrom = t('invalid_date');
    errors.validUntil = t('invalid_date');
  }

  if (state.validUntil) {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (state.validUntil < now) {
      errors.validUntil = t('end_date_in_past');
    }
  }

  const numericValue = Number(state.discountValue);
  if (!state.discountValue) {
    errors.discountValue = t('required_field');
  } else if (
    Number.isNaN(numericValue) ||
    numericValue < PROMOTION_VALUE_MIN ||
    numericValue > PROMOTION_VALUE_MAX
  ) {
    errors.discountValue = t('promotion_value_range_error');
  }

  if (!hasAnyChargerSelected(state.chargers)) {
    errors.chargers = t('charger_selection_required');
  }

  const englishLabel = state.labels[PROMOTION_LANGUAGES.ENGLISH];
  if (!englishLabel.trim()) {
    errors.labels[PROMOTION_LANGUAGES.ENGLISH] = t('required_field');
  } else if (englishLabel.length > RECEIPT_LABEL_MAX_LENGTH) {
    errors.labels[PROMOTION_LANGUAGES.ENGLISH] = t('exceeds_character_limit');
  }

  state.visibleOptionalLanguages.forEach((lang) => {
    if (state.labels[lang].length > RECEIPT_LABEL_MAX_LENGTH) {
      errors.labels[lang] = t('exceeds_character_limit');
    }
  });

  return errors;
};

export const hasFormErrors = (errors: PromotionFormErrors): boolean => {
  const { labels, ...topLevel } = errors;
  return Object.values(topLevel).some(Boolean) || Object.values(labels).some(Boolean);
};

const buildLabelMap = (state: PromotionFormState): Record<string, string> => {
  const label: Record<string, string> = {
    [PROMOTION_LANGUAGES.ENGLISH]: state.labels[PROMOTION_LANGUAGES.ENGLISH].trim(),
  };
  OPTIONAL_PROMOTION_LANGUAGES.forEach((lang: PromotionLanguage) => {
    if (!state.visibleOptionalLanguages.includes(lang)) return;
    const value = state.labels[lang].trim();
    if (value) label[lang] = value;
  });
  return label;
};

export const buildCreatePromotionPayload = (
  state: PromotionFormState,
  companyId: string,
): PromotionCreateRequest => ({
  companyId,
  name: state.name.trim(),
  label: buildLabelMap(state),
  internalNote: null,
  validFrom: state.validFrom ? state.validFrom.toISOString() : null,
  validUntil: state.validUntil ? state.validUntil.toISOString() : null,
  discountType: state.discountType,
  discountValue: Number(state.discountValue),
  applicableResources: buildApplicableResources(state.chargers),
});

export const buildUpdatePromotionPayload = (state: PromotionFormState): PromotionUpdateRequest => ({
  name: state.name.trim(),
  label: buildLabelMap(state),
  internalNote: null,
  validFrom: state.validFrom ? state.validFrom.toISOString() : null,
  validUntil: state.validUntil ? state.validUntil.toISOString() : null,
  discountType: state.discountType,
  discountValue: Number(state.discountValue),
  applicableResources: buildApplicableResources(state.chargers),
});
